import { FormErrors } from 'app/components/forms/error-message';
import {
  ControlledFormElement,
  DefaultLayoutForm,
  FormButtons
} from 'app/components/forms/layout';
import { SelectMultiHierarchy } from 'app/components/forms/select-multi-hierarchy';
import { SensitivitySelector } from 'app/components/inputs/selectors/sensitivity-selector';
import { Anatomy } from 'lib/models/diagnoses/choices/localized-findings/anatomy';
import { AssociatedSymptom } from 'lib/models/diagnoses/choices/localized-findings/associated-symptom';
import { Color } from 'lib/models/diagnoses/choices/localized-findings/color';
import { Decreased } from 'lib/models/diagnoses/choices/localized-findings/decreased';
import { Distribution } from 'lib/models/diagnoses/choices/localized-findings/distribution';
import { LesionConfig } from 'lib/models/diagnoses/choices/localized-findings/lesion-config';
import { LesionSurface } from 'lib/models/diagnoses/choices/localized-findings/lesion-surface';
import { LesionType } from 'lib/models/diagnoses/choices/localized-findings/lesion-type';
import { Onset } from 'lib/models/diagnoses/choices/localized-findings/onset';
import { Palpation } from 'lib/models/diagnoses/choices/localized-findings/palpation';
import { Pattern } from 'lib/models/diagnoses/choices/localized-findings/pattern';
import { Provoked } from 'lib/models/diagnoses/choices/localized-findings/provoked';
import { Quality } from 'lib/models/diagnoses/choices/localized-findings/quality';
import { Severity } from 'lib/models/diagnoses/choices/localized-findings/severity';
import { Sensitivity } from 'lib/models/diagnoses/choices/sensitivity';
import { Pain, Rash, Swelling } from 'lib/models/diagnoses/localized-finding';
import { ClassList } from 'lib/utils/react/classlist';
import React, { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { LuCheckCircle, LuPlusCircle } from 'react-icons/lu';

// Default sensitivity
const INITIAL_SENSITIVITY: Sensitivity = Sensitivity.IMPORTANT;

// Form schemas
type PainValues = {
  sensitivity: Sensitivity;
  radiates: Anatomy[];
  onset: Onset[];
  time: Pattern[];
  provoked: Provoked[];
  decreased: Decreased[];
  quality: Quality[];
  severity: Severity[];
};

type RashValues = {
  sensitivity: Sensitivity;
  radiates: Anatomy[];
  distribution: Distribution[];
  lesionType: LesionType[];
  lesionConfig: LesionConfig[];
  lesionSurface: LesionSurface[];
  color: Color[];
  associatedSymptom: AssociatedSymptom[];
  palpation: Palpation[];
};

type SwellingValues = {
  sensitivity: Sensitivity;
  radiates: Anatomy[];
  onset: Onset[];
  time: Pattern[];
  provoked: Provoked[];
  decreased: Decreased[];
};

// Need to have static fields on interface for this to work,
// or all localized attributes must implement some common interface
// or extend an abstract base class
// interface LocalizedModel {
//   readonly id: number;
//   readonly label: string;
//   values(): LocalizedModel[];
// }

interface MultiSelect2Props<T extends { readonly id: number; readonly label: string }> {
  model: { values(): T[] };
  value: T[];
  onChange: (items: T[]) => void;
}

const MultiSelect2 = <T extends { readonly id: number; readonly label: string }>({
  model,
  value,
  onChange
}: MultiSelect2Props<T>) => {
  // Sort by label
  const values = useMemo(() => {
    const values = model.values();
    return values;
    // return model !== Onset
    //   ? values.sort((a, b) => a.label.localeCompare(b.label))
    //   : values;
  }, [model]);

  // Build easy lookup
  const lookup = useMemo(() => {
    const lookup = new Map<number, any>();
    value.forEach((item) => lookup.set(item.id, item));
    return lookup;
  }, [value]);

  return (
    <div className="multi-select-2">
      {values.map((item) => {
        const selected = lookup.has(item.id);
        const classes = ClassList.of('select-item').add('selected', selected);
        return (
          <div
            key={item.id}
            className={classes.names}
            onClick={() => {
              if (selected) {
                const index = value.indexOf(item);
                if (index >= 0) {
                  const newValues = [...value];
                  newValues.splice(index, 1);
                  onChange(newValues);
                }
              } else {
                onChange([...value, item].sort((a, b) => a.id - b.id));
              }
            }}
          >
            {selected ? <LuCheckCircle /> : <LuPlusCircle />}
            <span>{item.label}</span>
          </div>
        );
      })}
    </div>
  );
};

export const PainForm: React.FC<{
  initial?: Pain;
  isShowSensitivity?: boolean;
  onCreated: (localizedFinding: Pain) => void;
}> = ({ initial, isShowSensitivity = true, onCreated }) => {
  const isNew = useMemo(() => (initial === undefined ? true : false), []);
  // Initialize with pain values or empty attributes if no initial is provided
  const form = useForm<PainValues>({
    defaultValues: {
      sensitivity: INITIAL_SENSITIVITY,
      radiates: initial?.radiates ?? [],
      onset: initial?.onset ?? [],
      time: initial?.time ?? [],
      provoked: initial?.provoked ?? [],
      decreased: initial?.decreased ?? [],
      quality: initial?.quality ?? [],
      severity: initial?.severity ?? []
    }
  });

  return (
    <DefaultLayoutForm
      onSubmit={form.handleSubmit((values: PainValues) => {
        const newPain = new Pain({ ...values, id: initial?.id });
        onCreated(newPain);
      })}
    >
      <h3>{isNew ? 'Add pain' : 'Edit pain'}</h3>

      {/* Global fields */}
      <FormErrors errors={form.formState.errors} />

      {/* Sensitivity */}
      {isShowSensitivity && (
        <Controller
          key="sensitivity"
          name="sensitivity"
          rules={{ required: 'Select estimated sensitivity.' }}
          control={form.control}
          render={({ field, fieldState }) => (
            <ControlledFormElement
              name={field.name}
              errors={form.formState.errors}
              description="Required. Select estimated sensitivity."
            >
              <label htmlFor={'pain-' + field.name}>Sensitivity</label>
              <SensitivitySelector
                id="pain-sensitivity"
                name={field.name}
                value={field.value}
                tabIndex={0}
                onChange={(selected) => field.onChange(selected)}
                required
                aria-invalid={!!fieldState.error}
              />
            </ControlledFormElement>
          )}
        />
      )}

      {/* Radiates */}
      <Controller
        key="radiates"
        name="radiates"
        rules={{ required: 'Please select at least one location.' }}
        control={form.control}
        render={({ field, fieldState }) => (
          <ControlledFormElement
            name={field.name}
            errors={form.formState.errors}
            description="Required. Select at least one location."
          >
            <label htmlFor={'pain-' + field.name}>
              Radiates{' '}
              <span className="text-subtle">
                ({field.value.length}/{Anatomy.size()})
              </span>
            </label>
            <SelectMultiHierarchy<Anatomy>
              model={Anatomy}
              fromKey={(k) => Anatomy.fromId(parseInt(k, 10))}
              toKey={(a) => a.id.toString()}
              selected={field.value}
              onChange={(newValue) => field.onChange(newValue)}
            />
          </ControlledFormElement>
        )}
      />

      {/* All other fields */}
      {[
        { field: 'onset', label: 'Onset', model: Onset },
        {
          field: 'time',
          label: 'Pattern',
          model: Pattern
        },
        {
          field: 'provoked',
          label: 'Provoked by',
          model: Provoked
        },
        {
          field: 'decreased',
          label: 'Decreased by',
          model: Decreased
        },
        {
          field: 'quality',
          label: 'Quality',
          model: Quality
        },
        {
          field: 'severity',
          label: 'Severity',
          model: Severity
        }
      ].map(({ field: fieldName, label, model }) => (
        <Controller
          key={fieldName as any}
          name={fieldName as any}
          control={form.control}
          render={({ field, fieldState }) => (
            <ControlledFormElement name={field.name} errors={form.formState.errors}>
              <label htmlFor={'pain-' + field.name}>
                {label}{' '}
                <span className="text-subtle">
                  ({field.value.length}/{model.values().length})
                </span>
              </label>
              <MultiSelect2
                model={model}
                value={field.value}
                onChange={(newValue) => field.onChange(newValue)}
              />
            </ControlledFormElement>
          )}
        />
      ))}
      <FormButtons>
        <button className="button button-primary">
          {isNew ? 'Add pain' : 'Update pain'}
        </button>
      </FormButtons>
    </DefaultLayoutForm>
  );
};

export const RashForm: React.FC<{
  initial?: Rash;
  isShowSensitivity?: boolean;
  onCreated: (localizedFinding: Rash) => void;
}> = ({ initial, isShowSensitivity = true, onCreated }) => {
  const isNew = useMemo(() => (initial === undefined ? true : false), []);
  // Initialize with pain values or empty attributes if no initial is provided
  const form = useForm<RashValues>({
    defaultValues: {
      sensitivity: INITIAL_SENSITIVITY,
      radiates: initial?.radiates ?? [],
      distribution: initial?.distribution ?? [],
      lesionType: initial?.lesionType ?? [],
      lesionConfig: initial?.lesionConfig ?? [],
      lesionSurface: initial?.lesionSurface ?? [],
      color: initial?.color ?? [],
      associatedSymptom: initial?.associatedSymptom ?? [],
      palpation: initial?.palpation ?? []
    }
  });

  return (
    <DefaultLayoutForm
      onSubmit={form.handleSubmit((values: RashValues) => {
        const newRash = new Rash({ ...values, id: initial?.id });
        onCreated(newRash);
      })}
    >
      <h3>{isNew ? 'Add rash' : 'Edit rash'}</h3>

      {/* Global fields */}
      <FormErrors errors={form.formState.errors} />

      {/* Sensitivity */}
      {isShowSensitivity && (
        <Controller
          key="sensitivity"
          name="sensitivity"
          rules={{ required: 'Select estimated sensitivity.' }}
          control={form.control}
          render={({ field, fieldState }) => (
            <ControlledFormElement
              name={field.name}
              errors={form.formState.errors}
              description="Required. Select estimated sensitivity."
            >
              <label htmlFor={'pain-' + field.name}>Sensitivity</label>
              <SensitivitySelector
                id="pain-sensitivity"
                name={field.name}
                value={field.value}
                tabIndex={0}
                onChange={(selected) => field.onChange(selected)}
                required
                aria-invalid={!!fieldState.error}
              />
            </ControlledFormElement>
          )}
        />
      )}

      {/* Radiates */}
      <Controller
        key="radiates"
        name="radiates"
        rules={{ required: 'Please select at least one location.' }}
        control={form.control}
        render={({ field, fieldState }) => (
          <ControlledFormElement
            name={field.name}
            errors={form.formState.errors}
            description="Required. Select at least one location."
          >
            <label htmlFor={'pain-' + field.name}>
              Radiates{' '}
              <span className="text-subtle">
                ({field.value.length}/{Anatomy.size()})
              </span>
            </label>
            <SelectMultiHierarchy<Anatomy>
              model={Anatomy}
              fromKey={(k) => Anatomy.fromId(parseInt(k, 10))}
              toKey={(a) => a.id.toString()}
              selected={field.value}
              onChange={(newValue) => field.onChange(newValue)}
            />
          </ControlledFormElement>
        )}
      />

      {/* Individual fields */}
      {[
        {
          field: 'distribution',
          label: 'Distribution',
          model: Distribution
        },
        {
          field: 'lesionType',
          label: 'Lesion type',
          model: LesionType
        },
        {
          field: 'lesionConfig',
          label: 'Lesion configuration',
          model: LesionConfig
        },
        {
          field: 'lesionSurface',
          label: 'Lesion surface',
          model: LesionSurface,
          selectableAllowed: false
        },
        { field: 'color', label: 'Color', model: Color },
        {
          field: 'associatedSymptom',
          label: 'Associated symptom',
          model: AssociatedSymptom
        },
        {
          field: 'palpation',
          label: 'Palpation',
          model: Palpation
        }
      ].map(({ field: fieldName, label, model }) => (
        <Controller
          key={fieldName as any}
          name={fieldName as any}
          control={form.control}
          render={({ field, fieldState }) => (
            <ControlledFormElement name={field.name} errors={form.formState.errors}>
              <label htmlFor={'pain-' + field.name}>
                {label}{' '}
                <span className="text-subtle">
                  ({field.value.length}/{model.values().length})
                </span>
              </label>
              <MultiSelect2
                model={model}
                value={field.value}
                onChange={(newValue) => field.onChange(newValue)}
              />
            </ControlledFormElement>
          )}
        />
      ))}
      <FormButtons>
        <button className="button button-primary">
          {isNew ? 'Add rash' : 'Update rash'}
        </button>
      </FormButtons>
    </DefaultLayoutForm>
  );
};

export const SwellingForm: React.FC<{
  initial?: Swelling;
  isShowSensitivity?: boolean;
  onCreated: (localizedFinding: Swelling) => void;
}> = ({ initial, isShowSensitivity = true, onCreated }) => {
  const isNew = useMemo(() => (initial === undefined ? true : false), []);
  // Initialize with pain values or empty attributes if no initial is provided
  const form = useForm<SwellingValues>({
    defaultValues: {
      sensitivity: INITIAL_SENSITIVITY,
      radiates: initial?.radiates ?? [],
      onset: initial?.onset ?? [],
      time: initial?.time ?? [],
      provoked: initial?.provoked ?? [],
      decreased: initial?.decreased ?? []
    }
  });

  return (
    <DefaultLayoutForm
      onSubmit={form.handleSubmit((values: SwellingValues) => {
        const newSwelling = new Swelling({ ...values, id: initial?.id });
        onCreated(newSwelling);
      })}
    >
      <h3>{isNew ? 'Add swelling' : 'Edit swelling'}</h3>

      {/* Global fields */}
      <FormErrors errors={form.formState.errors} />

      {/* Sensitivity */}
      {isShowSensitivity && (
        <Controller
          key="sensitivity"
          name="sensitivity"
          rules={{ required: 'Select estimated sensitivity.' }}
          control={form.control}
          render={({ field, fieldState }) => (
            <ControlledFormElement
              name={field.name}
              errors={form.formState.errors}
              description="Required. Select estimated sensitivity."
            >
              <label htmlFor={'pain-' + field.name}>Sensitivity</label>
              <SensitivitySelector
                id="pain-sensitivity"
                name={field.name}
                value={field.value}
                tabIndex={0}
                onChange={(selected) => field.onChange(selected)}
                required
                aria-invalid={!!fieldState.error}
              />
            </ControlledFormElement>
          )}
        />
      )}

      {/* Radiates */}
      <Controller
        key="radiates"
        name="radiates"
        rules={{ required: 'Please select at least one location.' }}
        control={form.control}
        render={({ field, fieldState }) => (
          <ControlledFormElement
            name={field.name}
            errors={form.formState.errors}
            description="Required. Select at least one location."
          >
            <label htmlFor={'pain-' + field.name}>
              Radiates{' '}
              <span className="text-subtle">
                ({field.value.length}/{Anatomy.size()})
              </span>
            </label>
            <SelectMultiHierarchy<Anatomy>
              model={Anatomy}
              fromKey={(k) => Anatomy.fromId(parseInt(k, 10))}
              toKey={(a) => a.id.toString()}
              selected={field.value}
              onChange={(newValue) => field.onChange(newValue)}
            />
          </ControlledFormElement>
        )}
      />

      {/* Individual fields */}
      {[
        { field: 'onset', label: 'Onset', model: Onset },
        {
          field: 'time',
          label: 'Pattern',
          model: Pattern
        },
        {
          field: 'provoked',
          label: 'Provoked by',
          model: Provoked
        },
        {
          field: 'decreased',
          label: 'Decreased by',
          model: Decreased
        }
      ].map(({ field: fieldName, label, model }) => (
        <Controller
          key={fieldName as any}
          name={fieldName as any}
          control={form.control}
          render={({ field, fieldState }) => (
            <ControlledFormElement name={field.name} errors={form.formState.errors}>
              <label htmlFor={'pain-' + field.name}>
                {label}{' '}
                <span className="text-subtle">
                  ({field.value.length}/{model.values().length})
                </span>
              </label>
              <MultiSelect2
                model={model}
                value={field.value}
                onChange={(newValue) => field.onChange(newValue)}
              />
            </ControlledFormElement>
          )}
        />
      ))}
      <FormButtons>
        <button className="button button-primary">
          {isNew ? 'Add swelling' : 'Update swelling'}
        </button>
      </FormButtons>
    </DefaultLayoutForm>
  );
};
